import React, { useCallback, useMemo, useState } from 'react';

import useToggle from '@lyearn/core/hooks/useToggle';
import { ArrowIosDownward } from '@lyearn/icons';
import { AutocompleteRenderOptionState, Loader, Stack, Typography } from '@lyearn/ui';
import AutocompleteSearch from '@/components/AutocompleteSearch';
import { PopperOverride } from '@/components/PopperOverride';
import useTagOptions from '@/modules/corporate/modules/tags/hooks/useTagOptions';
import { TagUnion } from '@/types/schema';

import { FilterValueProps } from '../types';
import { reRenderComponent } from './utils';

const TagValueSelector = (props: FilterValueProps) => {
  const {
    tag,
    onFilterValueChange,
    filterValue,
    className,
    classes,
    size = 'medium',
    placeholder = 'Search',
  } = props;
  const { id } = tag as TagUnion;
  const [searchText, setSearchText] = useState('');
  const [selectedValue, setSelectedValue] = useState<any>(null);
  const [open, { on, off }] = useToggle(true);

  const { error, tagOptions, fetchMore, fetching, isFetchingMore } = useTagOptions({
    tagId: id,
    valueText: searchText,
  });

  const { tagOptions: initialTags, fetching: fetchingInitials } = useTagOptions(
    {
      tagId: id,
      valueIds: [filterValue as string],
    },
    !filterValue,
  );

  const _onChange = useCallback(
    (id?: string, item?: any | null) => {
      onFilterValueChange(id);
      setSelectedValue(item);
      reRenderComponent({ on, off });
    },
    [onFilterValueChange],
  );
  const initialTag = useMemo(() => {
    return initialTags?.[0] ?? {};
  }, [initialTags]);
  const onInputChange = useCallback(
    (val: string) => {
      setSearchText(val || '');
    },
    [setSearchText],
  );
  const renderItemContent = useCallback(
    (
      option: {
        id: string;
        name: string;
      },
      state: AutocompleteRenderOptionState,
    ) => {
      return (
        <Stack alignItems="center" className="w-full h-[4rem] align-center ps-8">
          <Typography variant="body-short-02"> {option.name} </Typography>
        </Stack>
      );
    },
    [],
  );
  return fetching || fetchingInitials || !open ? (
    <Stack
      alignItems="center"
      className={className ?? 'w-[20rem] h-[4rem]'}
      justifyContent="center">
      <Loader />
    </Stack>
  ) : (
    <AutocompleteSearch
      key={`tags-and-properties-value-selector${tag?.id}`}
      blurOnSelect
      PopperComponent={(val) => (
        <PopperOverride className="h-[20rem]" placement="top-start" {...val} />
      )}
      className={className ?? 'w-[20rem]'}
      classes={classes}
      closeIcon={null}
      data={tagOptions}
      data-id={`tags-and-properties-value-selector${tag?.id}`}
      defaultValue={initialTag}
      fetchMore={fetchMore}
      initialValue={initialTag?.name}
      placeholder={placeholder}
      popupIcon={<ArrowIosDownward className="h-20 w-20" fillColorToken="icon-placeholder" />}
      renderers={{
        renderOption: renderItemContent,
      }}
      size={size}
      value={selectedValue}
      onChange={_onChange}
      onInputChange={onInputChange}
    />
  );
};

export default TagValueSelector;
